import { Box, Paper, TableRow, Typography } from '@mui/material'
import AdvancedTableWrapper from 'components/AdvancedTable'
import { LabelBox } from 'components/label-box'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { PrizeDrawRequest } from 'types/prize-draw'
import { renderCells } from 'utils/renderCells'
import { ToggleRequestState } from './toggle-request-state'
import { RequestStatusTag } from './request-status-tag'
import { RequestDetailsModal } from './request-details-modal'
import { Heading } from 'context/FiltersContext/FiltersContext.types'

export type RequestsTableProps = {
  marketId: number
  type: 'prize-embargo' | 'prize-draw'
}

export const EmbargoRequestsTable = ({ marketId, type }: RequestsTableProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'prizesManagement' })

  const isEmbargo = type === 'prize-embargo'

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Typography
        border="1px solid"
        borderColor="grey.200"
        sx={{ backgroundColor: 'grey.50', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
        px={2.5}
        pt={1}
        fontWeight={600}
        color="grey.900"
        pb={1}
        position="relative"
        mb="-12px"
      >
        {t('requests')}
      </Typography>
      <Paper
        sx={{
          pt: 2.25,
          px: 2,
          gap: 2,
          pb: 2.25,
          flex: 1,
          zIndeX: 2,
        }}
      >
        <Box width="100%">
          <AdvancedTableWrapper<PrizeDrawRequest>
            endpoint={`${type}/requests/${marketId}?order=DESC`}
            headings={[
              { type: 'empty', name: t('table.createdAt'), field: 'createdAt', width: 160 },
              { type: 'empty', name: t('table.status'), field: 'status', width: 120 },
              { type: 'empty', name: t('table.testEmailDate'), field: 'status', width: 120 },
              { type: 'empty', name: t('table.sendSampleDate'), field: 'status', width: 120 },
              { type: 'empty', name: t('table.sendAllDataDate'), field: 'status', width: 120 },
              ...((isEmbargo
                ? []
                : [{ type: 'empty', name: t('table.author'), field: 'user', width: 180 }]) as Heading[]),
              { type: 'empty', name: t('table.details'), field: '', width: 60 },
            ]}
            disableSearchParams
            name={`${type}-requests`}
            disableFilters
            options={{}}
            renderRow={row => (
              <TableRow>
                {renderCells([
                  {
                    label: t('table.createdAt'),
                    value: <LabelBox second={format(new Date(row.createdAt), 'MMM dd, yyyy')} />,
                  },
                  {
                    label: t('table.status'),
                    value: (
                      <Box display="flex" gap={1} alignItems="center">
                        <RequestStatusTag status={row.status} />
                        {(row.status === 'onhold' || row.status === 'inprogress' || row.status === 'new') && (
                          <ToggleRequestState type={type} requestId={row.id} status={row.status} />
                        )}
                      </Box>
                    ),
                  },
                  {
                    label: t('table.testEmailDate'),
                    value: row.testEmailDate ? (
                      <LabelBox
                        first={format(new Date(row.testEmailDate), 'MMM dd, yyyy')}
                        second={format(new Date(row.testEmailDate), 'HH:mm:ss')}
                      />
                    ) : (
                      '-'
                    ),
                  },
                  {
                    label: t('table.sendSampleDate'),
                    value: row.sendSampleDate ? (
                      <LabelBox
                        first={format(new Date(row.sendSampleDate), 'MMM dd, yyyy')}
                        second={format(new Date(row.sendSampleDate), 'HH:mm:ss')}
                      />
                    ) : (
                      '-'
                    ),
                  },
                  {
                    label: t('table.sendAllDataDate'),
                    value: row.sendAllDataDate ? (
                      <LabelBox
                        first={format(new Date(row.sendAllDataDate), 'MMM dd, yyyy')}
                        second={format(new Date(row.sendAllDataDate), 'HH:mm:ss')}
                      />
                    ) : (
                      '-'
                    ),
                  },
                  !isEmbargo
                    ? {
                        label: t('table.customer'),
                        value: <LabelBox first={row.user?.name} second={row.user?.email} />,
                      }
                    : null,
                  {
                    label: t('table.details'),
                    value: <RequestDetailsModal type={type} requestId={row.id} />,
                  },
                ])}
              </TableRow>
            )}
          />
        </Box>
      </Paper>
    </Box>
  )
}
